import styled from "styled-components"
import { MobergFontFamily, MobergFontSize } from "../../Components/MobergFont/MobergFont"
import { getStatusPalette } from "../../Components/StatusPill/statusPalette"

/* The look of Clara's Documents page, shared by the page, the side panel and the upload
   dialog. Moved from the Backstage Clara documents tab, which the page replaces. */

export const Center = styled.div`
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 200px;
`

export const Buttons = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 10px;
	align-items: center;
`

export const ModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	/* The app's own modals sit at 10000 (ModalsRenderer), over the header and the sidebar
	   with its toggle button (1200 and 2000), and dim with black at 0.4. */
	z-index: 10000;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.4);
`

const Modal = styled.div`
	width: min(480px, calc(100vw - 32px));
	padding: 22px;
	border-radius: 6px;
	background: var(--color-surface-panel, #fff);
	box-shadow: var(--modal-box-shadow);
	display: flex;
	flex-direction: column;
	gap: 12px;
`

export const ModalTitle = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 16px;
	font-weight: 650;
	color: var(--text-color);
`

export const ModalHint = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	color: var(--text-secondary-color);
	margin-top: -6px;
`

export const DropZone = styled.button`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	padding: 26px 12px;
	border: 1.5px dashed ${({ $over }) => ($over ? "var(--color-accent-interactive)" : "var(--color-border-default)")};
	border-radius: 10px;
	background: ${({ $over }) => ($over ? "var(--color-hover-neutral)" : "transparent")};
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13.5px;
	color: var(--text-secondary-color);
	cursor: pointer;

	&:hover {
		border-color: var(--color-accent-interactive);
		color: var(--text-color);
	}
`

export const ModalFooter = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	margin-top: 6px;
`

export const HiddenInput = styled.input`
	display: none;
`

export const Banner = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 0 8px 12px;
	padding: 10px 16px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	border-radius: 6px;
	/* --color-danger-text-hover, not --color-danger-text: the lighter red measured about
	   2.9:1 on its tinted background, too faint for the line that says policies are out
	   of date (UI review, 2 Oct). */
	color: ${({ $tone }) => ($tone === "error" ? "var(--color-danger-text-hover)" : $tone === "warn" ? "var(--text-color)" : "var(--text-secondary-color)")};
	background: ${({ $tone }) => ($tone === "error" ? "var(--color-danger-hover-bg)" : "var(--color-hover-neutral)")};
	border: 1px solid ${({ $tone }) => ($tone === "error" ? "var(--color-danger-text)" : "var(--color-border-default)")};
`

export const Failure = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	color: var(--color-danger-text);
`

export const Muted = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	color: var(--text-secondary-color);
`

export const Empty = styled.div`
	padding: 40px 16px;
	text-align: center;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	color: var(--text-secondary-color);
`

export const DateField = styled.label`
	display: flex;
	flex-direction: column;
	gap: 4px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	color: var(--text-secondary-color);

	input {
		font-size: 14px;
		padding: 6px 8px;
		border-radius: 6px;
		border: 1px solid var(--color-border-default);
		background: var(--color-input-bg, #fff);
		color: var(--text-color);
	}
`

export const SearchRow = styled.div`
	display: flex;
	gap: 10px;
	align-items: center;
`

export const SearchInput = styled.input`
	flex: 1;
	min-width: 0;
	padding: 9px 12px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	color: var(--text-color);
	background: var(--color-input-bg);
	border: 1px solid var(--color-border-default);
	border-radius: 6px;

	&:focus {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: 1px;
	}
`

export const Results = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-top: 10px;
	max-height: 420px;
	overflow-y: auto;
`

export const ResultsMeta = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	color: var(--text-secondary-color);
`

export const Result = styled.div`
	padding: 10px 12px;
	border: 1px solid var(--color-border-default);
	border-radius: 6px;
	background: var(--color-surface-panel);
`

export const ResultHead = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
	margin-bottom: 6px;
`

export const Rank = styled.span`
	font-family: monospace;
	font-size: 11px;
	font-weight: 700;
	color: var(--text-secondary-color);
	min-width: 16px;
`

export const ResultCitation = styled.span`
	flex: 1;
	min-width: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	font-weight: 600;
	color: var(--text-color);
`

export const Scores = styled.div`
	display: flex;
	gap: 6px;
	flex-wrap: wrap;
`

export const Score = styled.span`
	font-family: monospace;
	font-size: 10px;
	padding: 2px 6px;
	border-radius: 8px;
	color: ${({ $strong }) => ($strong ? "var(--color-success-text)" : "var(--text-secondary-color)")};
	background: ${({ $strong }) => ($strong ? "var(--color-success-hover-bg)" : "var(--color-hover-neutral)")};
`

export const ResultText = styled.p`
	margin: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	line-height: 1.5;
	color: var(--text-secondary-color);
`

export const PageContainer = styled.div`
	padding: 24px 30px;
	background: var(--color-surface-panel);
	height: calc(100vh - 60px);
	display: flex;
	flex-direction: column;
	gap: 12px;
	box-sizing: border-box;
	position: relative;
	overflow: hidden;
`

export const SortButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 2px;
	padding: 0;
	border: none;
	background: none;
	font: inherit;
	color: ${({ $active }) => ($active ? "var(--text-color)" : "inherit")};
	text-transform: inherit;
	letter-spacing: inherit;
	cursor: pointer;
`

export const Panel = styled.aside`
	flex: 0 1 400px;
	min-width: 300px;
	min-height: 0;
	display: flex;
	flex-direction: column;
	background: var(--color-surface-panel, #fff);
	border-left: 1px solid var(--color-border-light);
`

export const PanelBody = styled.div`
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 18px;
	padding: 20px 24px 24px;
`

export const WideModal = styled(Modal)`
	width: min(640px, calc(100vw - 32px));
	max-height: calc(100vh - 64px);
`

export const ProgressList = styled.div`
	display: flex;
	flex-direction: column;
	max-height: 320px;
	overflow-y: auto;
	border: 1px solid var(--color-border-light);
	border-radius: 8px;
`

export const ProgressRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 12px;
	border-bottom: 1px solid var(--color-border-light);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	color: var(--text-color);

	&:last-child {
		border-bottom: none;
	}
`

// The name keeps its room next to a long reason; the reason wraps instead.
export const ProgressName = styled.span`
	flex: 1 1 45%;
	min-width: 140px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

export const ProgressReason = styled.span`
	flex: 1 1 55%;
	min-width: 0;
	font-size: 12px;
	color: var(--text-secondary-color);
	overflow-wrap: anywhere;
`

export const LinkButton = styled.button`
	align-self: flex-end;
	padding: 0;
	border: none;
	background: none;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	color: var(--color-accent-interactive);
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}
`

export const PanelFooter = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 14px 24px;
	border-top: 1px solid var(--color-border-light);
`

// Header and type badge in the style of Backstage's Environment Variables section.
export const SectionHeader = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 0 8px 12px;
	border-bottom: 1px solid var(--color-border-default);
`

export const PageTitle = styled.h2`
	font-family: ${MobergFontFamily.HEADER};
	font-size: 20px;
	font-weight: 600;
	color: var(--color-text-primary);
	margin: 0;
`

export const SearchWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex: 1 1 260px;
	padding: 6px 12px;
	border: 1px solid var(--color-border-default);
	border-radius: 4px;
	background: var(--color-surface-panel);

	&:focus-within {
		border-color: var(--color-accent-interactive);
	}

	input {
		width: 100%;
		border: none;
		outline: none;
		background: transparent;
		font-family: ${MobergFontFamily.REGULAR};
		font-size: 14px;
		color: var(--color-text-primary);
	}

	input::placeholder {
		color: var(--text-secondary-color);
	}
`

export const TypeWrap = styled.span`
	display: inline-flex;
	align-self: flex-start;
	align-items: center;
	gap: 6px;
	max-width: 100%;
	min-width: 0;
`

export const TypePill = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	padding: 3px 10px;
	border-radius: 6px;
	border: 1px solid var(--color-border-light);
	background: var(--color-hover-neutral);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.3px;
	text-transform: uppercase;
	color: var(--text-secondary-color);
`

export const PlainType = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	color: var(--color-text-primary);
`

// The shared status pill palette (PreloadStatusPill): info, warning, danger.
export const StatusTag = styled.span`
	align-self: flex-start;
	flex-shrink: 0;
	padding: 2px 10px;
	border-radius: 16px;
	border: 1px solid ${({ $tone }) => getStatusPalette($tone).border};
	background: ${({ $tone }) => getStatusPalette($tone).background};
	color: ${({ $tone }) => getStatusPalette($tone).color};
	font-family: ${MobergFontFamily.REGULAR};
	font-size: ${MobergFontSize.SMALL};
	font-weight: 600;
	white-space: nowrap;
`

export const PreviewFrame = styled.iframe`
	flex: 1;
	width: 100%;
	border: none;
	background: var(--color-hover-neutral);
`


export const IconButton = styled.button`
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	padding: 0;
	border: none;
	border-radius: 8px;
	background: none;
	color: var(--text-secondary-color);
	cursor: pointer;

	&:hover {
		background: var(--color-hover-neutral);
		color: var(--color-text-primary);
	}

	&:focus-visible {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: 1px;
	}
`

export const Toolbar = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px 12px;
	padding: 0 8px 10px;
	border-bottom: 1px solid var(--color-border-light);

	select {
		height: 32px;
		padding: 0 8px;
		border: 1px solid var(--color-border-light);
		border-radius: 6px;
		background: var(--color-surface-panel);
		font-family: inherit;
		font-size: 13px;
		color: var(--color-text-primary);
	}
`

export const ViewButton = styled.button`
	height: 32px;
	padding: 0 12px;
	border: none;
	border-radius: 6px;
	background: ${({ $active }) => ($active ? "var(--color-hover-blue)" : "transparent")};
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	font-weight: ${({ $active }) => ($active ? 600 : 500)};
	color: ${({ $active }) => ($active ? "var(--color-accent-interactive)" : "var(--color-text-primary)")};
	cursor: pointer;

	&:hover {
		background: ${({ $active }) => ($active ? "var(--color-hover-blue)" : "var(--color-hover-neutral)")};
	}

	span {
		margin-left: 6px;
		font-size: 12px;
		font-weight: 400;
		color: var(--text-secondary-color);
	}
`

export const Split = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	margin: 0 -30px -24px;
	border-top: 1px solid var(--color-border-light);
`

export const ListPane = styled.div`
	flex: 1 1 auto;
	min-width: 0;
	overflow: auto;
`

export const ColHead = styled.div`
	position: sticky;
	top: 0;
	z-index: 1;
	height: 36px;
	box-sizing: border-box;
	padding: 0 24px 0 62px;
	display: flex;
	align-items: center;
	gap: 18px;
	background: var(--color-surface-panel);
	border-bottom: 1px solid var(--color-border-light);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	font-weight: 600;
	color: var(--text-secondary-color);

	button {
		font-size: 12px;
		font-weight: 600;
	}
`

export const GroupHead = styled.div`
	height: 38px;
	box-sizing: border-box;
	padding: 0 24px 0 20px;
	display: flex;
	align-items: center;
	gap: 8px;
	background: var(--color-surface-primary);
	border-bottom: 1px solid var(--color-border-light);
	font-family: ${MobergFontFamily.REGULAR};

	button {
		font-size: 13px;
	}
`

export const GroupLabel = styled.span`
	font-size: 13px;
	font-weight: 600;
	color: var(--color-text-primary);
	white-space: nowrap;
`

export const GroupHint = styled.span`
	margin-left: 8px;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 13px;
	color: var(--text-secondary-color);
`

export const DocRow = styled.div`
	height: 48px;
	box-sizing: border-box;
	padding: 0 24px 0 28px;
	display: flex;
	align-items: center;
	gap: 18px;
	border-bottom: 1px solid var(--color-border-light);
	font-family: ${MobergFontFamily.REGULAR};

	&:hover {
		background: var(--color-table-row-hover-bg);
	}
`

export const DocTitle = styled.button`
	flex: 1 1 auto;
	min-width: 0;
	height: 46px;
	padding: 0;
	border: none;
	background: none;
	display: flex;
	align-items: center;
	gap: 10px;
	text-align: left;
	font-family: inherit;
	color: var(--color-text-primary);
	cursor: pointer;

	span {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-size: 14px;
		font-weight: 500;
	}

	em {
		flex-shrink: 1;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-style: normal;
		font-size: 12px;
		color: var(--text-secondary-color);
	}

	&:focus-visible {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: 2px;
	}
`

export const GuessChip = styled.span`
	height: 24px;
	box-sizing: border-box;
	padding: 0 8px;
	display: inline-flex;
	align-items: center;
	border: 1px dashed #B26A00;
	border-radius: 4px;
	font-size: 12px;
	color: #8A5300;
	white-space: nowrap;
`

export const TinyButton = styled.button`
	height: 24px;
	padding: 0 8px;
	border: 1px solid var(--color-accent-interactive);
	border-radius: 4px;
	background: var(--color-surface-panel);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	font-weight: 500;
	color: var(--color-accent-interactive);
	cursor: pointer;

	&:hover:not(:disabled) {
		background: var(--color-hover-blue);
	}

	&:disabled {
		opacity: 0.5;
		cursor: default;
	}
`

export const PropGrid = styled.dl`
	display: grid;
	grid-template-columns: 96px minmax(0, 1fr);
	grid-auto-rows: minmax(32px, auto);
	align-items: center;
	gap: 2px 12px;
	margin: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;

	dt {
		font-weight: 400;
		color: var(--text-secondary-color);
	}

	dd {
		margin: 0;
		min-width: 0;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		color: var(--color-text-primary);
	}
`

const REVIEW_TONE = {
	overdue: { border: "#F3B4B4", status: "var(--color-danger-text-hover)" },
	current: { border: "var(--color-border-light)", status: "#2E7D32" },
	none: { border: "var(--color-border-light)", status: "var(--text-secondary-color)" },
}

export const ReviewCard = styled.section`
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding: 16px;
	border: 1px solid ${({ $tone }) => REVIEW_TONE[$tone].border};
	border-radius: 10px;
	font-family: ${MobergFontFamily.REGULAR};

	h3 {
		flex-grow: 1;
		margin: 0;
		font-size: 13px;
		font-weight: 600;
		color: var(--color-text-primary);
	}

	.status {
		font-size: 12px;
		font-weight: 600;
		white-space: nowrap;
		color: ${({ $tone }) => REVIEW_TONE[$tone].status};
	}

	.date {
		font-size: 20px;
		font-weight: 600;
		color: var(--color-text-primary);
	}

	p {
		margin: 0;
		font-size: 13px;
		line-height: 1.5;
		color: var(--text-color);
	}

	.actions {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding-top: 12px;
		border-top: 1px solid ${({ $tone }) => REVIEW_TONE[$tone].border};
	}
`

export const Crumbs = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 0 8px 12px;
	border-bottom: 1px solid var(--color-border-light);
	font-family: ${MobergFontFamily.REGULAR};
`

export const DocMain = styled.main`
	flex: 1 1 auto;
	min-width: 0;
	min-height: 0;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding: 24px 32px;
	font-family: ${MobergFontFamily.REGULAR};

	h1 {
		margin: 0;
		font-family: ${MobergFontFamily.HEADER};
		font-size: 24px;
		font-weight: 600;
		line-height: 1.3;
		color: var(--color-text-primary);
	}
`

export const DocTabs = styled.div`
	display: flex;
	gap: 4px;
	border-bottom: 1px solid var(--color-border-light);
`

export const DocTab = styled.button`
	height: 40px;
	margin-bottom: -1px;
	padding: 0 12px;
	border: none;
	border-bottom: 2px solid ${({ $active }) => ($active ? "var(--color-accent-interactive)" : "transparent")};
	background: none;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	font-weight: ${({ $active }) => ($active ? 600 : 400)};
	color: ${({ $active }) => ($active ? "var(--color-accent-interactive)" : "var(--color-text-primary)")};
	cursor: pointer;
`

export const PdfBox = styled.div`
	flex: 1 1 auto;
	min-height: 560px;
	display: flex;
	flex-direction: column;
	border: 1px solid var(--color-border-light);
	border-radius: 8px;
	overflow: hidden;
`

export const PdfBar = styled.div`
	height: 40px;
	box-sizing: border-box;
	padding: 0 12px;
	display: flex;
	align-items: center;
	gap: 10px;
	background: var(--color-surface-primary);
	border-bottom: 1px solid var(--color-border-light);
	font-size: 13px;
	color: var(--text-secondary-color);
`
